import React from 'react';

import {RequiredMark} from 'src/components/required-mark';
import {VendorApprovalRule} from 'src/openapi/admin/models/VendorApprovalRule';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import Typography from '@pb/kit-component-typography';

import {useAddRuleDialog} from '../../hooks/use-add-rule-dialog';

import {GrantSuggestList} from './grant-suggest-list';


import type {Grant} from 'src/openapi/admin/models/Grant';

type Props = {
    shown: boolean,
    vendorId?: string,
    existingRules: VendorApprovalRule[],
    availableGrants: Grant[],
    onClose: () => void,
    onRuleCreated: (rule: VendorApprovalRule) => void,
};

export const AddRuleDialog: React.FC<Props> = ({
    shown,
    vendorId,
    existingRules,
    availableGrants,
    onClose,
    onRuleCreated,
}) => {
    const {
        ruleName,
        grantQuery,
        grantResults,
        grantSuggestShown,
        searchLoading,
        hasSearched,
        isMinQueryShown,
        shouldShowNoGrants,
        isCreateDisabled,
        busy,
        displayError,
        handleGrantQueryChange,
        handleGrantSelect,
        handleGrantFocus,
        setGrantSuggestShown,
        handleRuleNameChange,
        handleCreateRule,
        handleClose,
        clearGrantSelection,
    } = useAddRuleDialog({
        vendorId,
        existingRules,
        initialGrants: availableGrants,
        onCreated: onRuleCreated,
        onClose,
    });

    const shouldShowSuggest = grantSuggestShown
        && (searchLoading || grantResults.length > 0 || hasSearched || isMinQueryShown || shouldShowNoGrants);

    return (
        <Dialog
            shown={shown}
            setShown={visible => {
                if (!visible) {
                    handleClose();
                }
            }}>
            <DialogHeader onClose={handleClose}>
                <Typography variant="heading-level-2">
                    Add New Rule
                </Typography>
            </DialogHeader>
            <Group direction="v" gap="m" style={{padding: 24, width: 400}}>
                {displayError ? (
                    <Message color="error" style={{paddingBottom: 4}}>
                        {displayError}
                    </Message>
                ) : null}
                <Item>
                    <Label htmlFor="rule-name-input">
                        <RequiredMark label="Rule Name"/>
                    </Label>
                    <Input
                        id="rule-name-input"
                        value={ruleName}
                        onChange={handleRuleNameChange}
                        placeholder="Enter rule name"
                        autoComplete="off"
                        autoCorrect="off"
                        maxLength={255}
                    />
                </Item>
                <Item>
                    <Label htmlFor="grant-name-input">
                        <RequiredMark label="Grant"/>
                    </Label>
                    <Autosuggest
                        id="grant-name-input"
                        value={grantQuery}
                        shown={shouldShowSuggest}
                        setShown={setGrantSuggestShown}
                        onChange={handleGrantQueryChange}
                        onFocus={handleGrantFocus}
                        placeholder="Search Grant by name or ID"
                        onItemSelect={handleGrantSelect}
                        onClear={clearGrantSelection}
                        onBlur={() => setGrantSuggestShown(false)}>
                        <GrantSuggestList
                            id="grant-suggest-list"
                            busy={searchLoading}
                            grants={grantResults}
                            hasSearched={hasSearched}
                            isMinQueryShown={isMinQueryShown}
                        />
                    </Autosuggest>
                </Item>
            </Group>
            <DialogFooter>
                <Button
                    variant="Type.PRIMARY"
                    disabled={isCreateDisabled}
                    busy={busy}
                    onClick={handleCreateRule}>
                    Create
                </Button>
                <Button onClick={handleClose} busy={busy} variant="Type.SECONDARY">
                    Cancel
                </Button>
            </DialogFooter>
        </Dialog>
    );
};
